'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import utilsStyles from '@/styles/modules/utils.module.scss';
import Image from 'next/image';
import type { BannerResponse } from '../../types';
import styles from './KvHero.module.scss';

const splideOptions = {
  type: 'fade',
  rewind: true,
  autoplay: true,
  interval: 6000,
  speed: 1000,
  arrows: false,
  pagination: false,
  pauseOnHover: false,
  perPage: 1,
};

const renderSlider = (images: BannerResponse[]) => (
  <Splide options={splideOptions} hasTrack={false} aria-label='ヒーロー画像'>
    <SplideTrack>
      {images.map((image) => (
        <SplideSlide key={image.id} className={styles.slide}>
          <Image src={image.imageUrl} alt='' fill priority sizes='100vw' quality={90} className={styles.image} />
        </SplideSlide>
      ))}
    </SplideTrack>
  </Splide>
);

// API からヒーロー画像を取得できなかった場合の public/ 直下フォールバック。
// パス文字列のまま next/image に渡し、スライダー画像と同様に最適化(webp/リサイズ)を効かせる。
// .slide でラップしてスライダー（複数枚）と同じレイヤー構造に収める。
const renderFallbackImage = (src: string) => (
  <div className={styles.slide}>
    <Image src={src} alt='' fill priority sizes='100vw' quality={90} className={styles.image} />
  </div>
);

function KvHero({ heroImagesPc, heroImagesSp }: { heroImagesPc: BannerResponse[]; heroImagesSp: BannerResponse[] }) {
  return (
    <div className={styles.container}>
      <div className={`${styles.sliderArea} ${utilsStyles.onlySp}`} aria-hidden='true'>
        {heroImagesSp.length > 0 ? renderSlider(heroImagesSp) : renderFallbackImage('/hero-fallback-image-sp.jpg')}
      </div>
      <div className={`${styles.sliderArea} ${utilsStyles.onlyPc}`} aria-hidden='true'>
        {heroImagesPc.length > 0 ? renderSlider(heroImagesPc) : renderFallbackImage('/hero-fallback-image-pc.jpg')}
      </div>
      <div className={styles.overlay} aria-hidden='true' />
      <div className={styles.content}>
        <h1 className={`${styles.headline} ${utilsStyles.paperTexture}`} data-text={'ボクらの\n原点を\n手に入れよう'}>
          ボクらの
          <br />
          <span className={styles.accent}>原点</span>を
          <br />
          手に入れよう
        </h1>
        <p className={styles.subtext}>
          あのコマが、Tシャツに。
          <br className={utilsStyles.onlySp} />
          トートに。
          <br />
          <br className={utilsStyles.onlySp} />
          <br className={utilsStyles.onlySp} />
          世界でひとつだけのグッズを、
          <br className={utilsStyles.onlySp} />
          今日から自分のくらしに。
        </p>
        <div className={styles.ctaRow}>
          <a href='#lineupSection' className={`${styles.ctaButton} ${styles.ctaPrimary}`}>
            <span className={styles.ctaLabel}>好きな作品を探す</span>
            <span className={utilsStyles.onlySp}>
              <RoundArrowIcon direction='right' color='white' size='small' />
            </span>
            <span className={utilsStyles.onlyPc}>
              <RoundArrowIcon direction='right' color='white' borderColor='white' size='small' />
            </span>
          </a>
          <LoadingLink href='/user-designs' className={`${styles.ctaButton} ${styles.ctaSecondary}`}>
            <span className={styles.ctaLabel}>
              みんなのデザイン
              <span className={utilsStyles.onlyPc}>を見る</span>
            </span>
            <RoundArrowIcon direction='right' color='black' size='small' />
          </LoadingLink>
        </div>
      </div>
    </div>
  );
}

export default KvHero;
